import React, { useMemo } from "react";
import { MetaSelector } from ".";
import { StatsOverview } from "./StatsOverview";
import { DashboardCharts } from "./DashboardCharts";
import { TeamPerformance } from "./TeamPerformance";
import { PeriodType, SalesData } from "../types";
import { MetaResumida } from "../../../../types/meta";

interface DashboardTabProps {
  selectedMeta: MetaResumida | null;
  metasDisponiveis: MetaResumida[];
  salesData: SalesData[];
  loading: boolean;
  error: string | null;
  onMetaChange: (meta: MetaResumida) => void;
  onRefresh: () => void;
}

export const DashboardTab: React.FC<DashboardTabProps> = ({
  selectedMeta,
  metasDisponiveis,
  salesData,
  loading,
  error,
  onMetaChange,
  onRefresh,
}) => {
  // Calcular estatísticas gerais baseadas nos dados reais
  const {
    totalTarget,
    totalAchieved,
    overallProgress,
    topPerformers,
    summaryData,
  } = useMemo(() => {
    if (!salesData.length) {
      return {
        totalTarget: 0,
        totalAchieved: 0,
        overallProgress: 0,
        topPerformers: [],
        summaryData: {
          totalTarget: 0,
          totalAchieved: 0,
          overAchievers: 0,
          underPerformers: 0,
        },
      };
    }

    const totalTarget = salesData.reduce(
      (sum: number, item: SalesData) => sum + item.target,
      0
    );
    const totalAchieved = salesData.reduce(
      (sum: number, item: SalesData) => sum + item.achieved,
      0
    );
    const overallProgress =
      totalTarget > 0 ? Math.round((totalAchieved / totalTarget) * 100) : 0;
    const topPerformers = salesData.filter(
      (item: SalesData) => item.achieved / item.target >= 1
    );
    const underPerformers = salesData.filter(
      (item: SalesData) => item.achieved / item.target < 1
    );

    return {
      totalTarget,
      totalAchieved,
      overallProgress,
      topPerformers,
      summaryData: {
        totalTarget,
        totalAchieved,
        overAchievers: topPerformers.length,
        underPerformers: underPerformers.length,
      },
    };
  }, [salesData]);

  return (
    <>
      <MetaSelector
        selectedMeta={selectedMeta}
        metasDisponiveis={metasDisponiveis}
        loading={loading}
        onMetaChange={onMetaChange}
        onRefresh={onRefresh}
      />

      <StatsOverview
        totalTarget={totalTarget}
        totalAchieved={totalAchieved}
        overallProgress={overallProgress}
        topPerformersCount={topPerformers.length}
        selectedMeta={selectedMeta}
      />

      <DashboardCharts
        salesData={salesData}
        summaryData={summaryData}
        loading={loading}
        error={error}
        onRefresh={onRefresh}
      />

      <TeamPerformance
        salesData={salesData}
        loading={loading}
        error={error}
        onRefresh={onRefresh}
      />
    </>
  );
};
